import {
	useGetAccountInsightsFinancialHistoryQuery,
	useGetAccountInsightsSummaryQuery,
} from "@api/accountManagement";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatMoney } from "@pages/profile-v2/utils";
import {
	CHART_COLORS,
	formatAxisValue,
	formatDateLabel,
	getBaseChartOptions,
	hasActivity,
} from "../../utils";

const FINANCIAL_CHUNK_SIZE = 15;

const isElement = (node) => node instanceof Element;

const useFinancialInfiniteScroll = ({
	onLoadMore,
	hasMore,
	loading,
	enabled = true,
	watchKey,
}) => {
	const scrollRootRef = useRef(null);
	const sentinelRef = useRef(null);
	const isFetchingRef = useRef(false);
	const [scrollRootElement, setScrollRootElement] = useState(null);
	const [sentinelElement, setSentinelElement] = useState(null);

	const setScrollRootRef = useCallback((node) => {
		scrollRootRef.current = node;
		setScrollRootElement(isElement(node) ? node : null);
	}, []);

	const setSentinelRef = useCallback((node) => {
		sentinelRef.current = node;
		setSentinelElement(isElement(node) ? node : null);
	}, []);

	const tryLoadMore = useCallback(async () => {
		if (!enabled || !hasMore || loading || isFetchingRef.current) {
			return;
		}

		isFetchingRef.current = true;

		try {
			await onLoadMore();
		} finally {
			isFetchingRef.current = false;
		}
	}, [enabled, hasMore, loading, onLoadMore]);

	const isSentinelVisible = useCallback(() => {
		const root = scrollRootRef.current;
		const sentinel = sentinelRef.current;

		if (!isElement(root) || !isElement(sentinel)) {
			return false;
		}

		const rootRect = root.getBoundingClientRect();
		const sentinelRect = sentinel.getBoundingClientRect();

		return sentinelRect.top <= rootRect.bottom + 48;
	}, []);

	useEffect(() => {
		if (!enabled || !hasMore || !scrollRootElement || !sentinelElement) {
			return undefined;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					tryLoadMore();
				}
			},
			{
				root: scrollRootElement,
				rootMargin: "48px",
				threshold: 0,
			}
		);

		observer.observe(sentinelElement);

		return () => observer.disconnect();
	}, [enabled, hasMore, scrollRootElement, sentinelElement, tryLoadMore]);

	useEffect(() => {
		if (!enabled || !hasMore || loading) {
			return;
		}

		if (isSentinelVisible()) {
			tryLoadMore();
		}
	}, [enabled, hasMore, loading, isSentinelVisible, tryLoadMore, watchKey]);

	return {
		scrollRootRef: setScrollRootRef,
		sentinelRef: setSentinelRef,
	};
};

const useController = ({ translate }) => {
	const [dateRange, setDateRange] = useState([moment().subtract(30, "days"), moment()]);
	const [interval, setInterval] = useState("day");
	const [visibleCount, setVisibleCount] = useState(FINANCIAL_CHUNK_SIZE);

	const queryParams = useMemo(
		() => ({
			from: dateRange[0].valueOf(),
			to: dateRange[1].valueOf(),
			interval,
		}),
		[dateRange, interval]
	);

	const financialQuery = useGetAccountInsightsFinancialHistoryQuery(queryParams, {
		refetchOnMountOrArgChange: true,
	});

	const { data: summaryData } = useGetAccountInsightsSummaryQuery();
	const currency = summaryData?.financial?.currency;

	const buckets = financialQuery.data?.buckets || [];

	useEffect(() => {
		setVisibleCount(FINANCIAL_CHUNK_SIZE);
	}, [buckets]);

	const hasMore = visibleCount < buckets.length;

	const handleLoadMore = useCallback(async () => {
		setVisibleCount((prev) => Math.min(prev + FINANCIAL_CHUNK_SIZE, buckets.length));
	}, [buckets.length]);

	const infiniteScroll = useFinancialInfiniteScroll({
		onLoadMore: handleLoadMore,
		hasMore,
		loading: false,
		enabled: buckets.length > FINANCIAL_CHUNK_SIZE,
		watchKey: visibleCount,
	});

	const historyChart = useMemo(() => {
		const deposits = buckets.map((bucket) => Number(bucket.deposits) || 0);
		const withdrawals = buckets.map((bucket) => Number(bucket.withdrawals) || 0);
		const netLoss = buckets.map((bucket) => Number(bucket.gamingNetLoss) || 0);

		const datasets = [
			hasActivity(deposits) && {
				label: translate("accountInsights.financialHistory.deposits"),
				data: deposits,
				backgroundColor: CHART_COLORS.secondaryFill,
				borderColor: CHART_COLORS.secondary,
				borderWidth: 2,
				borderRadius: 6,
				borderSkipped: false,
				maxBarThickness: 28,
			},
			hasActivity(withdrawals) && {
				label: translate("accountInsights.financialHistory.withdrawals"),
				data: withdrawals,
				backgroundColor: CHART_COLORS.tertiaryFill,
				borderColor: CHART_COLORS.tertiary,
				borderWidth: 2,
				borderRadius: 6,
				borderSkipped: false,
				maxBarThickness: 28,
			},
			hasActivity(netLoss) && {
				label: translate("accountInsights.financialHistory.netLoss"),
				data: netLoss,
				backgroundColor: CHART_COLORS.dangerFill,
				borderColor: CHART_COLORS.danger,
				borderWidth: 2,
				borderRadius: 6,
				borderSkipped: false,
				maxBarThickness: 28,
			},
		].filter(Boolean);

		if (!datasets.length) {
			return null;
		}

		return {
			data: {
				labels: buckets.map((bucket) => formatDateLabel(bucket.date)),
				datasets,
			},
			options: {
				...getBaseChartOptions(),
				plugins: {
					...getBaseChartOptions().plugins,
					legend: {
						...getBaseChartOptions().plugins.legend,
						labels: {
							...getBaseChartOptions().plugins.legend.labels,
							usePointStyle: true,
							pointStyle: "rectRounded",
						},
					},
					tooltip: {
						...getBaseChartOptions().plugins.tooltip,
						callbacks: {
							label: (context) => {
								const value = context.parsed.y;
								return `${context.dataset.label}: ${formatMoney(value, currency)}`;
							},
						},
					},
				},
				scales: {
					x: {
						...getBaseChartOptions().scales.x,
						grid: { display: false },
						stacked: false,
					},
					y: {
						...getBaseChartOptions().scales.y,
						stacked: false,
						beginAtZero: true,
						grace: "8%",
						ticks: {
							...getBaseChartOptions().scales.y.ticks,
							callback: (value) => formatAxisValue(value, currency),
						},
					},
				},
			},
		};
	}, [buckets, currency, translate]);

	const tableData = useMemo(
		() =>
			buckets.slice(0, visibleCount).map((bucket) => ({
				...bucket,
				key: bucket.date,
			})),
		[buckets, visibleCount]
	);

	return {
		state: {
			buckets,
			isLoading: financialQuery.isLoading,
			isError: financialQuery.isError,
			totals: financialQuery.data?.totals,
			historyChart,
			currency,
			tableData,
			hasMore,
			interval,
		},
		handlers: {
			refetch: financialQuery.refetch,
			setDateRange,
			setInterval,
		},
		infiniteScroll,
	};
};

export default useController;
